import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { Data } from '@generated/Foundation.GMI.model';
import { getEnumValue } from 'lib/utils/sitecore/enum';

export const fieldTypes = [
  'ImageLabel',
  'Image',
  'Eyebrow',
  'Title',
  'ProductNumber',
  'Rating',
  'KitchenTested',
  'PrepTime',
  'TotalTime',
  'Ingredients',
  'Servings',
  'Description',
  'Favorite',
  'Author',
  'Headline',
  'Labels',
  'PublishDate',
  'CreatedDate',
] as const;

export type CardFields = (typeof fieldTypes)[number];

export type ContentOverride = {
  overrideContent?: string;
  title?: string;
  removeTitleLink?: boolean;
  image?: string;
  tag?: string;
  description?: string;
  url?: string;
  ctaText?: string;
  // When true, the card must render as non-interactive (no link). Set for a
  // timed release override that has not been released yet.
  hideLink?: boolean;
  // Release date (Sitecore datetime) carried through when the card is a
  // not-yet-released timed release, so assistive technology can announce when
  // the hidden content becomes available.
  releaseDate?: string;
};

// Same shape as ContentOverride with the additional `releaseDate` field from
// the Timed Release Override template.
export type TimedReleaseOverride = ContentOverride & {
  releaseDate?: string;
};

// Parses a Sitecore datetime value (e.g. "20260731T000100Z") into epoch ms.
// Returns null when the value is missing or unparseable.
const parseSitecoreDateTime = (value?: string): number | null => {
  if (!value) {
    return null;
  }
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/.exec(value);
  const normalized = match
    ? `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}Z`
    : value;
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Decides which override (if any) should be applied to a card, following this
 * precedence:
 *   1. If a timed release override exists AND its releaseDate is in the future
 *      (releaseDate > now), use the timed release override.
 *   2. Otherwise, if a standard content override exists, use it.
 *   3. Otherwise return undefined so the card renders with its existing data.
 */
export const resolveEffectiveOverride = (
  timedOverride: TimedReleaseOverride | undefined,
  contentOverride: ContentOverride | undefined,
  now: number = Date.now()
): ContentOverride | undefined => {
  if (timedOverride) {
    const releaseTime = parseSitecoreDateTime(timedOverride.releaseDate);
    if (releaseTime !== null && releaseTime > now) {
      // Locked timed release: show the teaser fields but suppress any link so
      // the real content stays hidden and the card is not clickable.
      return { ...timedOverride, hideLink: true };
    }
  }

  if (contentOverride) {
    return contentOverride;
  }

  return undefined;
};

export const DEFAULT_CONTENT_PAGE_SIZE = 12;

export const resolveContentPageSize = (...candidates: Array<number | null | undefined>): number => {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return candidate;
    }
  }

  return DEFAULT_CONTENT_PAGE_SIZE;
};

type NormalizableImageUrl =
  | string
  | {
      src?: string;
    }
  | null
  | undefined;

// Utility function for normalizing image URLs
export const normalizeStandardImageUrl = (
  imageUrl?: NormalizableImageUrl,
  pageUrl?: string
): string => {
  const resolvedImageUrl =
    typeof imageUrl === 'string'
      ? imageUrl
      : imageUrl && typeof imageUrl === 'object' && typeof imageUrl.src === 'string'
        ? imageUrl.src
        : '';

  if (!resolvedImageUrl) return '';

  // Handle protocol-relative URLs (//example.com/image.jpg)
  if (resolvedImageUrl.startsWith('//')) {
    return resolvedImageUrl.replace(/^\/\//, 'https://');
  }

  // Convert absolute Sitecore media URLs (e.g. "https://<host>/-/media/...") to a
  // relative "/-/media/..." path. This lets the Next.js "/-/:path*" rewrite proxy
  // it to the Sitecore host, keeps it environment-agnostic (no hardcoded CM/site
  // host), and allows next/image to optimize it.
  const mediaPathIndex = resolvedImageUrl.indexOf('/-/media/');
  if (mediaPathIndex > 0 && /^https?:\/\//i.test(resolvedImageUrl)) {
    return resolvedImageUrl.slice(mediaPathIndex);
  }

  // Preserve Sitecore media paths so local rewrites and CM hosting can resolve them correctly.
  if (resolvedImageUrl.startsWith('/-/media/')) {
    return resolvedImageUrl;
  }

  // Handle domain-relative URLs (/image.jpg) - need domain from pageUrl
  if (resolvedImageUrl.startsWith('/') && pageUrl) {
    try {
      const url = new URL(pageUrl);
      return `https://${url.host}${resolvedImageUrl}`;
    } catch {
      // pageUrl is not an absolute URL (e.g. also relative); return resolvedImageUrl as-is
      return resolvedImageUrl;
    }
  }

  // For all other URLs (absolute URLs), return as is
  return resolvedImageUrl;
};

export function getImageSettingsByContentType(
  contentGridSettings: Data.Settings.ContentGridSettings | null,
  contentType?: string
) {
  const defaultAspectRatio = getEnumValue<ImageAspectRatio>(
    contentGridSettings?.fields?.defaultCardImageAspectRatio
  );
  const images =
    (contentGridSettings?.fields
      ?.contentGridImage as Data.Settings.ContentGridImageOverride_Item[]) ?? [];

  const item = images.find((item) => item.fields?.contentType?.name === contentType);
  const result = {
    aspectRatio: getEnumValue<ImageAspectRatio>(item?.fields?.aspectRatio) ?? defaultAspectRatio,
    forceWhiteBackground: item?.fields?.forceWhiteBackground?.value ?? false,
  };
  return result;
}
